import './StaffPhotosView.css'

import { useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../../store'

import { Button } from '../../../components/Button'
import { DataTable } from '../../../components/DataTable'
import { EmptyState } from '../../../components/EmptyState'
import { FilterBar } from '../../../components/FilterBar'
import { MediaTile } from '../../../components/MediaTile'
import { SegmentedControl } from '../../../components/SegmentedControl'
import { SelectField } from '../../../components/SelectField'
import { Tag } from '../../../components/Tag'
import { TextField } from '../../../components/TextField'

const viewModeKey = 'staffPhotosViewMode'

type ViewMode = 'list' | 'tiles'

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

const sortItems = [
  { value: 'createdAt', text: 'Upload time' },
  { value: 'processedAt', text: 'Processed time' },
  { value: 'status', text: 'Status' },
  { value: 'attractiveness', text: 'Attractiveness' },
  { value: 'quality', text: 'Photo quality' },
]

export function StaffPhotosView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const photos = useAtomValue(staff.staffPhotosAtom)
  const total = useAtomValue(staff.staffPhotosTotalAtom)
  const isLoading = useAtomValue(staff.staffPhotosIsLoadingAtom)
  const error = useAtomValue(staff.staffPhotosErrorAtom)
  const query = useAtomValue(staff.staffPhotoQueryAtom)
  const actionLoading = useAtomValue(staff.staffActionIsLoadingAtom)

  const setQuery = useSetAtom(staff.setStaffPhotoQueryAtom)
  const fetchPhotos = useSetAtom(staff.fetchStaffPhotosAtom)
  const approvePhoto = useSetAtom(staff.approveStaffPhotoAtom)
  const rejectPhoto = useSetAtom(staff.rejectStaffPhotoAtom)

  const canRead = staff.hasStaffPermission(session, staff.Permission.ModerationPhotosRead)
  const canWrite = staff.hasStaffPermission(session, staff.Permission.ModerationPhotosWrite)

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(viewModeKey)
    return stored === 'tiles' ? 'tiles' : 'list'
  })
  const [searchDraft, setSearchDraft] = useState(query.q)
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({})

  useEffect(() => {
    localStorage.setItem(viewModeKey, viewMode)
  }, [viewMode])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft !== query.q) setQuery({ q: searchDraft })
    }, 300)
    return () => window.clearTimeout(handle)
  }, [searchDraft, query.q, setQuery])

  useEffect(() => {
    if (canRead) fetchPhotos()
  }, [canRead, fetchPhotos, query])

  useEffect(() => {
    let cancelled = false
    const objectUrls: string[] = []

    async function loadThumbs() {
      const next: Record<string, string> = {}
      for (const photo of photos) {
        const objectUrl = await staff.fetchStaffPhotoObjectUrl(photo.id, 320)
        if (cancelled) {
          if (objectUrl) URL.revokeObjectURL(objectUrl)
          continue
        }
        if (objectUrl) {
          next[photo.id] = objectUrl
          objectUrls.push(objectUrl)
        }
      }
      if (!cancelled) setThumbnails(next)
    }

    if (photos.length > 0) loadThumbs()
    else setThumbnails({})

    return () => {
      cancelled = true
      for (const url of objectUrls) URL.revokeObjectURL(url)
    }
  }, [photos])

  const columns = useMemo(
    () => [
      {
        id: 'thumb',
        header: '',
        width: '7rem',
        render: (photo: staff.StaffPhoto) =>
          thumbnails[photo.id] ? (
            <img className="staff-photos__thumb" src={thumbnails[photo.id]} alt="" />
          ) : (
            <div className="staff-photos__thumb staff-photos__thumb--empty" />
          ),
      },
      {
        id: 'status',
        header: 'Status',
        render: (photo: staff.StaffPhoto) => (
          <Tag color={staff.statusTagColor(photo.status)}>{photo.status}</Tag>
        ),
      },
      {
        id: 'description',
        header: 'Recognition',
        render: (photo: staff.StaffPhoto) => (
          <div className="staff-photos__desc">
            <span>{staff.photoDescription(photo) || '—'}</span>
            {staff.photoFeatures(photo).length > 0 && (
              <span className="staff-photos__features">
                {staff.photoFeatures(photo).join(', ')}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: 'Uploaded',
        render: (photo: staff.StaffPhoto) => formatDate(photo.createdAt),
      },
      {
        id: 'ids',
        header: 'IDs',
        render: (photo: staff.StaffPhoto) => (
          <div className="staff-photos__ids">
            <span>photo {shortId(photo.id)}</span>
            <span>user {shortId(photo.userId)}</span>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        render: (photo: staff.StaffPhoto) =>
          canWrite ? (
            <div className="staff-photos__row-actions">
              <Button
                kind="secondary"
                size="small"
                text="Approve"
                disabled={actionLoading || photo.status === 'approved'}
                onClick={() => approvePhoto(photo.id)}
              />
              <Button
                kind="destructive"
                size="small"
                text="Reject"
                disabled={actionLoading || photo.status === 'rejected'}
                onClick={() => rejectPhoto(photo.id)}
              />
            </div>
          ) : null,
      },
    ],
    [thumbnails, canWrite, actionLoading, approvePhoto, rejectPhoto],
  )

  if (!canRead) {
    return (
      <EmptyState
        title="No photo access"
        description="Your role does not include moderation.photos.read."
      />
    )
  }

  return (
    <div className="staff-photos">
      <FilterBar>
        <div className="staff-photos__search">
          <TextField
            label="Search recognition"
            kind="search"
            placeholder="Description, features, flags, ids…"
            value={searchDraft}
            onChange={setSearchDraft}
          />
        </div>
        <SegmentedControl
          ariaLabel="Photo status"
          value={query.status}
          onChange={(status) => setQuery({ status })}
          options={statusOptions}
        />
        <div className="staff-photos__sort">
          <SelectField
            label="Sort"
            items={sortItems}
            value={query.sort}
            onChange={(sort) => setQuery({ sort })}
          />
        </div>
        <SegmentedControl
          ariaLabel="View mode"
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: 'list', label: 'List' },
            { value: 'tiles', label: 'Tiles' },
          ]}
        />
        <Button
          kind="secondary"
          text={isLoading ? 'Loading…' : 'Refresh'}
          onClick={() => fetchPhotos()}
          disabled={isLoading}
        />
      </FilterBar>

      <p className="staff-photos__count">
        {isLoading ? 'Loading…' : `${total.toLocaleString()} photo${total === 1 ? '' : 's'}`}
      </p>

      {error === 'forbidden' && (
        <EmptyState title="Forbidden" description="You cannot list photos with this role." />
      )}
      {error && error !== 'forbidden' && (
        <EmptyState
          title="Failed to load photos"
          description={error === 'network' ? 'Network error.' : 'Request failed.'}
          actions={<Button kind="secondary" text="Retry" onClick={() => fetchPhotos()} />}
        />
      )}

      {!error && !isLoading && photos.length === 0 && (
        <EmptyState
          title="No photos match"
          description="Try a different search or status filter."
        />
      )}

      {!error && photos.length > 0 && viewMode === 'list' && (
        <DataTable
          columns={columns}
          rows={photos}
          getRowKey={(photo) => photo.id}
          emptyMessage="No photos match"
        />
      )}

      {!error && photos.length > 0 && viewMode === 'tiles' && (
        <div className="staff-photos__grid">
          {photos.map((photo) => (
            <MediaTile
              key={photo.id}
              imageSrc={thumbnails[photo.id]}
              imageAlt={staff.photoDescription(photo) || 'Photo'}
              badge={<Tag color={staff.statusTagColor(photo.status)}>{photo.status}</Tag>}
              title={staff.photoDescription(photo) || `Photo ${shortId(photo.id)}`}
              meta={`${formatDate(photo.createdAt)} · user ${shortId(photo.userId)}`}
              footer={
                <>
                  {staff
                    .photoFeatures(photo)
                    .slice(0, 4)
                    .map((feature) => (
                      <Tag key={feature} color="violet">
                        {feature}
                      </Tag>
                    ))}
                  {canWrite && (
                    <div className="staff-photos__tile-actions">
                      <Button
                        kind="secondary"
                        size="small"
                        text="Approve"
                        disabled={actionLoading || photo.status === 'approved'}
                        onClick={() => approvePhoto(photo.id)}
                      />
                      <Button
                        kind="destructive"
                        size="small"
                        text="Reject"
                        disabled={actionLoading || photo.status === 'rejected'}
                        onClick={() => rejectPhoto(photo.id)}
                      />
                    </div>
                  )}
                </>
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

function shortId(id: string) {
  return id.slice(-6)
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}
